/**
 * =============================================================================
 * WlSubscriptionKpiGrid
 *
 * Grid de 4 KPIs no topo da aba Assinaturas do painel WL. Segue o padrão
 * visual da referência Stripe:
 *
 *   1. Assinantes ativos            — total atual + linha 12 meses
 *   2. Crescimento de assinantes    — saldo do mês + waterfall (novos↑/cancel.↓)
 *   3. Ticket médio por tenant      — MRR/ativos + linha 12 meses (substitui
 *                                     "Novos assinantes" da referência)
 *   4. Assinantes perdidos          — cancelados no mês + linha 12 meses
 *
 * Dados vindos da RPC `wl_get_financial_kpis` (trend_tenants + trend_revenue).
 *
 * @version 1.2.0
 * RELEASE:
 *   2026-07-22 v1.0.0 - Versão inicial.
 *   2026-07-22 v1.1.0 - KPIs clicáveis (Assinantes ativos / Crescimento /
 *                       Assinantes perdidos) abrem WlFinancialDetailModal
 *                       reutilizando o type `paying` com statuses pré-filtrados.
 *   2026-07-22 v1.2.0 - Fonte única de verdade: drill-down passa a filtrar pelo
 *                       mês corrente (SP) via novos parâmetros da RPC v1.7.0,
 *                       alinhando quantidade do card com a listagem.
 * =============================================================================
 */
import { useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, TrendingUp, DollarSign, UserX } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useWlFinancialKpis } from '@/hooks/wl/useWlFinancialKpis';
import {
  WlKpiSparkline,
  type SparkLinePoint,
  type SparkWaterfallPoint,
} from './WlKpiSparkline';
import {
  WlFinancialDetailModal,
  type WlFinancialDetailType,
} from './WlFinancialDetailModal';
import { getCurrentMonthRangeSP } from './utils/monthRangeSP';

interface Props {
  /** Status efetivos do filtro global (mesmo que a Visão Geral usa). */
  statuses?: string[];
}

/** Estado do modal de drill-down (mesmo shape usado no Overview). */
interface ModalState {
  open: boolean;
  type: WlFinancialDetailType;
  title: string;
  statuses?: string[];
  /** v1.2.0: janelas de período opcionais (mês corrente SP). */
  createdFrom?: string | null;
  createdTo?: string | null;
  canceledFrom?: string | null;
  canceledTo?: string | null;
  periodOr?: boolean;
  /** v1.3.0: janela sobre tenants.deactivated_at (card "Assinantes perdidos"). */
  deactivatedFrom?: string | null;
  deactivatedTo?: string | null;
}

const fmtBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number(v || 0),
  );

/**
 * Card KPI grande com título, valor principal e sparkline abaixo.
 * Se `onClick` for informado, o card ganha estilo clicável e dispara a ação.
 */
function KpiBigCard({
  icon: Icon,
  label,
  value,
  sub,
  children,
  deltaPct,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: React.ReactNode;
  sub?: string;
  children: React.ReactNode;
  deltaPct?: number;
  onClick?: () => void;
}) {
  const showDelta = typeof deltaPct === 'number' && !Number.isNaN(deltaPct);
  const positive = (deltaPct ?? 0) >= 0;
  const clickable = typeof onClick === 'function';
  return (
    <Card
      className={cn(
        'p-4 flex flex-col gap-2',
        clickable &&
          'cursor-pointer transition-colors hover:bg-muted/30 hover:border-primary/40',
      )}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Icon className="h-4 w-4" />
          <span>{label}</span>
        </div>
        {showDelta && (
          <span
            className={cn(
              'text-[10px] font-medium',
              positive
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400',
            )}
          >
            {positive ? '+' : ''}
            {deltaPct!.toFixed(1)}%
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-semibold leading-none">{value}</span>
        {sub && <span className="text-[11px] text-muted-foreground">{sub}</span>}
      </div>
      {children}
    </Card>
  );
}

export function WlSubscriptionKpiGrid({ statuses }: Props) {
  const { data, isLoading } = useWlFinancialKpis(
    null,
    'America/Sao_Paulo',
    statuses && statuses.length ? statuses : null,
  );

  const derived = useMemo(() => {
    if (!data) return null;

    const trendT = data.trend_tenants ?? [];
    const trendR = data.trend_revenue ?? [];

    // Linha "Assinantes ativos": active_total mês a mês
    const activeLine: SparkLinePoint[] = trendT.map((p) => ({
      month: p.month,
      value: Number(p.active_total || 0),
    }));

    // Waterfall crescimento: novos positivos, cancelados como negativos
    const growthWaterfall: SparkWaterfallPoint[] = trendT.map((p) => ({
      month: p.month,
      positive: Number(p.new_tenants || 0),
      negative: -Number(p.churned_tenants || 0),
    }));

    // Linha "Assinantes perdidos": churned mês a mês
    const lostLine: SparkLinePoint[] = trendT.map((p) => ({
      month: p.month,
      value: Number(p.churned_tenants || 0),
    }));

    // Linha "Ticket médio por tenant": MRR total / ativos por mês
    const avgTicketLine: SparkLinePoint[] = trendT.map((p, idx) => {
      const active = Number(p.active_total || 0);
      const mrr = Number(trendR[idx]?.mrr || 0);
      return {
        month: p.month,
        value: active > 0 ? mrr / active : 0,
      };
    });

    const lastT = trendT[trendT.length - 1];
    const prevT = trendT[trendT.length - 2];

    const activeNow = Number(data.kpis?.paying_tenants?.value ?? lastT?.active_total ?? 0);
    const activePrev = Number(prevT?.active_total ?? 0);
    const activeDelta =
      activePrev > 0 ? ((activeNow - activePrev) / activePrev) * 100 : undefined;

    const newThisMonth = Number(lastT?.new_tenants ?? 0);
    const lostThisMonth = Number(lastT?.churned_tenants ?? 0);
    const growth = newThisMonth - lostThisMonth;

    const avgTicketNow = Number(data.kpis?.avg_ticket?.value ?? 0);
    const avgTicketPrev = avgTicketLine[avgTicketLine.length - 2]?.value ?? 0;
    const avgTicketDelta =
      avgTicketPrev > 0
        ? ((avgTicketNow - avgTicketPrev) / avgTicketPrev) * 100
        : undefined;

    const lostPrev = Number(prevT?.churned_tenants ?? 0);
    const lostDelta =
      lostPrev > 0 ? ((lostThisMonth - lostPrev) / lostPrev) * 100 : undefined;

    return {
      activeLine,
      growthWaterfall,
      lostLine,
      avgTicketLine,
      activeNow,
      activeDelta,
      newThisMonth,
      lostThisMonth,
      growth,
      avgTicketNow,
      avgTicketDelta,
      lostDelta,
    };
  }, [data]);

  // Estado do modal de drill-down (Assinantes ativos / perdidos / crescimento).
  const [modal, setModal] = useState<ModalState>({
    open: false,
    type: 'paying',
    title: '',
  });

  /** Abre o modal parametrizado com statuses pré-filtrados. */
  const openModal = (partial: Omit<ModalState, 'open'>) =>
    setModal({ ...partial, open: true });

  if (isLoading || !derived) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-[200px] w-full" />
        ))}
      </div>
    );
  }

  // Janela do mês corrente em SP (fonte única de verdade dos KPIs mensais).
  const { startISO: monthStart, endISO: monthEnd, label: monthLabel } =
    getCurrentMonthRangeSP();

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <KpiBigCard
          icon={Users}
          label="Assinantes ativos"
          value={derived.activeNow}
          deltaPct={derived.activeDelta}
          onClick={() =>
            openModal({
              type: 'paying',
              title: 'Assinantes ativos',
              statuses: ['active'],
              // Sem filtro de período: valor é snapshot atual (não mensal).
            })
          }
        >
          <WlKpiSparkline mode="line" data={derived.activeLine} />
        </KpiBigCard>

        <KpiBigCard
          icon={TrendingUp}
          label="Crescimento de assinantes"
          value={
            <span className={derived.growth >= 0 ? '' : 'text-red-600'}>
              {derived.growth >= 0 ? '+' : ''}
              {derived.growth}
            </span>
          }
          sub={`${derived.newThisMonth} novos · ${derived.lostThisMonth} cancel.`}
          onClick={() =>
            openModal({
              type: 'paying',
              title: `Crescimento de assinantes — ${monthLabel} (novos + cancelados)`,
              // Sem statuses fixo: usa OR entre criados e cancelados no mês.
              createdFrom: monthStart,
              createdTo: monthEnd,
              canceledFrom: monthStart,
              canceledTo: monthEnd,
              periodOr: true,
            })
          }
        >
          <WlKpiSparkline mode="waterfall" data={derived.growthWaterfall} />
        </KpiBigCard>

        <KpiBigCard
          icon={DollarSign}
          label="Ticket médio por tenant"
          value={fmtBRL(derived.avgTicketNow)}
          deltaPct={derived.avgTicketDelta}
        >
          <WlKpiSparkline
            mode="line"
            data={derived.avgTicketLine}
            formatValue={fmtBRL}
          />
        </KpiBigCard>

        <KpiBigCard
          icon={UserX}
          label="Assinantes perdidos"
          value={derived.lostThisMonth}
          deltaPct={derived.lostDelta}
          onClick={() =>
            openModal({
              type: 'paying',
              title: `Assinantes perdidos — ${monthLabel}`,
              // v1.3.0: usa deactivated_at (mesma fonte do KPI churned_tenants
              // em wl_get_financial_kpis v1.8.0). Cobre tenants desativados
              // por qualquer motivo — canceled / suspended / inactive —
              // garantindo paridade 100% entre o número do card e a lista.
              deactivatedFrom: monthStart,
              deactivatedTo: monthEnd,
            })
          }
        >
          <WlKpiSparkline
            mode="line"
            data={derived.lostLine}
            color="#f5a524"
          />
        </KpiBigCard>
      </div>

      {/* ===================== Drill-down: lista de tenants ===================== */}
      <WlFinancialDetailModal
        open={modal.open}
        onOpenChange={(open) => setModal((m) => ({ ...m, open }))}
        type={modal.type}
        title={modal.title}
        statuses={modal.statuses}
        createdFrom={modal.createdFrom}
        createdTo={modal.createdTo}
        canceledFrom={modal.canceledFrom}
        canceledTo={modal.canceledTo}
        periodOr={modal.periodOr}
        deactivatedFrom={modal.deactivatedFrom}
        deactivatedTo={modal.deactivatedTo}
      />
    </>
  );
}
